"use client";

import { useQueries } from "@tanstack/react-query";

import {
  CONVERSATION_ALL_GET,
  getAllConversations,
} from "@/api/client/conversation";
import { attachParticipantsToConversations } from "@/api/client/participant";
import { useDebounce } from "@/hooks/useDebounce";
import { Conversation } from "@/types";

const SEARCH_PAGE_SIZE = 20;

/** Newest first, on the same field the picker's subtitle shows. */
const recordedTime = (conversation: Conversation): number => {
  const recordedAt = conversation.recordedAt ?? conversation.createdAt;
  if (!recordedAt) return 0;
  const time = new Date(
    typeof recordedAt === "string" ? recordedAt : Number(recordedAt),
  ).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Meetings a deliverable may pull in, searched server-side across the firm —
 * the wanted meeting may belong to a client who is not attached.
 *
 * An empty box is a real query: with clients attached it lists the recent
 * meetings of *any* of them, and once the advisor types the clients stop
 * narrowing anything.
 *
 * `GET /conversation` filters by one `clientId`, so several attached clients
 * mean one request each, merged here. They are separate cache entries on
 * purpose: attaching a third client reuses the two already fetched.
 */
export const useConversationSearch = (search: string, clientIds: string[]) => {
  const debouncedSearch = useDebounce(search, 500);
  const trimmed = debouncedSearch.trim();

  // A search is firm-wide, so it collapses to the single unfiltered request no
  // matter how many clients are attached.
  const scopes = trimmed || clientIds.length === 0 ? [null] : clientIds;

  const results = useQueries({
    queries: scopes.map((clientId) => ({
      queryKey: [
        CONVERSATION_ALL_GET,
        "deliverable-context",
        trimmed,
        clientId,
      ],
      queryFn: async () => {
        const rows = await getAllConversations({
          take: SEARCH_PAGE_SIZE,
          asc: false,
          includeTranscript: false,
          ...(trimmed ? { search: trimmed } : {}),
          ...(clientId ? { clientId } : {}),
        });
        return attachParticipantsToConversations(rows);
      },
    })),
  });

  // Derived on every render rather than memoized: `useQueries` hands back a
  // fresh array each time, so a dependency list could only be a digest of it —
  // and merging at most a few pages of rows is cheaper than computing one.
  //
  // Each scope keeps an equal share of the page rather than the merge being
  // capped as a whole: one client with twenty recent meetings would otherwise
  // fill the list and leave the second client — the reason the advisor
  // attached them — with nothing on offer.
  const perScope = Math.max(1, Math.floor(SEARCH_PAGE_SIZE / scopes.length));

  // A meeting both clients attended comes back from both requests.
  const byId = new Map<string, Conversation>();
  for (const result of results) {
    const rows = [...(result.data ?? [])]
      .sort((a, b) => recordedTime(b) - recordedTime(a))
      .slice(0, perScope);
    for (const conversation of rows) {
      if (!byId.has(conversation.id)) byId.set(conversation.id, conversation);
    }
  }

  const conversations = Array.from(byId.values()).sort(
    (a, b) => recordedTime(b) - recordedTime(a),
  );

  return {
    conversations,
    isLoading: results.some((result) => result.isFetching),
    // One client's meetings failing still leaves the others pickable, so the
    // picker only shows an error when nothing loaded at all.
    isError: results.length > 0 && results.every((result) => result.isError),
    error: results.find((result) => result.error)?.error ?? null,
  };
};
